import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

import type * as THREE from 'three';

/**
 * Information about an animation clip extracted from a GLTF file
 */
export interface GltfAnimationInfo {
  /** Name of the animation clip */
  name: string;
  /** Duration in seconds */
  duration: number;
  /** Reference to the original THREE.AnimationClip */
  clip: THREE.AnimationClip;
}

/**
 * Result of GLTF animation extraction
 */
export interface GltfAnimationExtractionResult {
  /** Array of animation clips found in the GLTF file */
  animations: GltfAnimationInfo[];
  /** Total number of animations */
  animationCount: number;
  /** Names of all animations */
  animationNames: string[];
}

/**
 * Extract animation clips from a GLTF/GLB file
 * @param gltfUrl - URL to the GLTF/GLB file
 * @returns Promise resolving to extraction result with animation information
 */
export async function extractAnimationsFromGltf(gltfUrl: string): Promise<GltfAnimationExtractionResult> {
  const loader = new GLTFLoader();

  return new Promise<GltfAnimationExtractionResult>((resolve, reject) => {
    loader.load(
      gltfUrl,
      (gltf) => {
        const animations: GltfAnimationInfo[] = [];

        if (gltf.animations && gltf.animations.length > 0) {
          gltf.animations.forEach((clip, index) => {
            animations.push({
              name: clip.name || `Animation_${index}`,
              duration: clip.duration,
              clip,
            });
          });
        }

        resolve({
          animations,
          animationCount: animations.length,
          animationNames: animations.map(anim => anim.name),
        });
      },
      undefined,
      (error) => {
        reject(new Error(`Failed to load GLTF file: ${error}`));
      }
    );
  });
}

/**
 * Check if a GLTF/GLB file has animations without loading the full scene
 * @param gltfUrl - URL to the GLTF/GLB file
 * @returns Promise resolving to true if animations exist, false otherwise
 */
export async function hasAnimations(gltfUrl: string): Promise<boolean> {
  try {
    const result = await extractAnimationsFromGltf(gltfUrl);
    return result.animationCount > 0;
  } catch (error) {
    console.error('Error checking for animations:', error);
    return false;
  }
}

